import styled from 'styled-components';
import { DateRange } from 'react-date-range';
import { colors, Select, Input, RadioFieldGroup, RadioFieldGroupProps } from '@dexma/ui-components';
import { rgba, transparentize } from 'polished';

// Due to the way infite scroll height on DateRange interacts with the dynamic number
// of periods on the Form, we need to interpolate the height of the DateRange
// somewhat clumsily, no other way found for now.
const calculateHeight = (numberOfElements: number) => {
    const x1 = 6;
    const y1 = 421 - 36;
    const x2 = 9;
    const y2 = 526 - 55;

    return `${((y2 - y1) * (numberOfElements - x1)) / (x2 - x1) + y1}px !important`;
};

type DateRangeProps = {
    isDateRangeDisabled: boolean;
    primaryColor: string;
    secondaryColor: string;
    numberOfElements: number;
    isToEnd: boolean;
};

export const StyledPeriodSelectorUI = styled.div`
    display: flex;
    width: 680px;
    //Antd popover padding cannot be locally overriden since the class is sub-rendered by the popover component.
    // so we compensate it here.
    margin: -12px;
`;
export const StyledFormColumn = styled.div`
    padding: 0 16px;
    width: 40%;
    margin-top: 16px;
`;

export const StyledSelectablePeriodTemplate = styled.div`
    display: flex;
    flex-direction: column;
`;
export const StyledDateRange = styled(DateRange)<DateRangeProps>`
    width: 100%;

    .rdrMonthPicker {
        select {
            width: auto !important;
            height: auto !important;
        }
    }

    .rdrYearPicker {
        select {
            width: auto !important;
            height: auto !important;
        }
    }

    .rdrMonthName {
        padding: 0;
        margin-top: 2em;
        margin-bottom: 0.5em;
    }

    .rdrMonth {
        width: auto;
    }

    .rdrDay {
        pointer-events: ${({ isDateRangeDisabled }) => (isDateRangeDisabled ? 'none' : 'auto')};
    }

    //We assign .rdrDayFuture className to all days after today from dayContentRenderer
    .rdrDay:has(.rdrDayNumber .rdrDayFuture) .rdrStartEdge,
    .rdrDay:has(.rdrDayNumber .rdrDayFuture) .rdrInRange,
    .rdrDay:has(.rdrDayNumber .rdrDayFuture) .rdrEndEdge {
        background-color: ${({ isToEnd, primaryColor, secondaryColor }) => (isToEnd ? primaryColor : secondaryColor)} !important;
    }

    // Half Colored Today
    .rdrDayToday .rdrInRange,
    .rdrDayToday .rdrEndEdge,
    .rdrDayToday .rdrEndEdge,
    .rdrDayToday .rdrStartEdge {
        background: ${({ isToEnd, primaryColor, secondaryColor }) =>
            !isToEnd ? `linear-gradient(to right, ${primaryColor} 50%, ${secondaryColor} 50%)` : primaryColor} !important;
    }

    .rdrDayToday.rdrDayPassive .rdrDayNumber span:after {
        background: ${({ primaryColor }) => primaryColor} !important;
    }

    .rdrDayToday > .rdrStartEdge span:after {
        background: white !important;
    }

    .rdrDayToday > .rdrInRange span:after {
        background: white !important;
    }

    .rdrDayToday > .rdrEndEdge span:after {
        background: white !important;
    }

    .rdrDayToday:not(:has(.rdrStartEdge)):not(:has(.rdrEndEdge)):not(:has(.rdrInRange)) span:after {
        background: ${({ primaryColor }) => primaryColor} !important;
    }

    .rdrDateDisplayWrapper {
        border-bottom: 1px solid ${colors.gray200};
    }

    .rdrMonthsVertical {
        min-height: 300px;
        height: ${({ numberOfElements }) => calculateHeight(numberOfElements)};
    }

    .rdrDateDisplayItem {
        border: 1px solid ${colors.gray300};
        box-shadow: none;
    }

    .rdrDateDisplayWrapper {
        background-color: transparent !important;
    }

    // Smoother color on calendar days when disabled
    .rdrDays button:not(.rdrDayPassive) .rdrDayNumber span {
        color: ${({ isDateRangeDisabled }) => (isDateRangeDisabled ? transparentize(0.5, '#849095') : undefined)};
    }

    .rdrDateInput input {
        color: ${({ isDateRangeDisabled }) => (isDateRangeDisabled ? transparentize(0.5, '#849095') : undefined)};
    }

    .rdrDays button:not(.rdrDayPassive) {
        .rdrStartEdge + .rdrDayNumber,
        .rdrInRange + .rdrDayNumber,
        .rdrEndEdge + .rdrDayNumber {
            span {
                color: white;
            }
        }
    }
`;

export const StyledPeriodTypeFieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;

    label {
        width: 50%;
        justify-content: center;
        font-weight: 500;
        color: ${colors.gray700};
    }

    span {
        font-weight: 500;
        color: ${colors.gray700};
        margin-bottom: 8px;
    }

    .period-type-description {
        color: ${colors.gray500};
        font-weight: 300;
        margin-top: 8px;
        font-size: 14px;
    }
`;

type RadioButtonWrapperProps = {
    checked: boolean;
    color: string;
    $isDisabled: boolean;
};
export const StyledRadioButtonWrapper = styled.label<RadioButtonWrapperProps>`
    display: inline-block;
    background-color: ${({ checked, color, $isDisabled }) => (checked && !$isDisabled ? color : colors.gray100)};
    border-radius: 4px;
    height: 25px;
    display: flex;
    align-items: center;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    margin-top: 3px;
    margin-bottom: 3px;
    opacity: ${({ $isDisabled }) => ($isDisabled ? '0.5' : '1')};
    color: ${({ checked, $isDisabled }) => (checked && !$isDisabled ? colors.white : colors.gray700)};

    :focus {
        outline: none;
    }

    input[type='radio'] {
        visibility: hidden;
    }
`;

export const StyledCustomizablePeriod = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    .customizable-period-title {
        font-weight: 500;
        color: ${colors.gray700};
    }
`;

export const StyledPeriodSelect = styled(Select)`
    margin-top: 4px;
    margin-bottom: 4px;
`;

export const StyledNumberInput = styled(Input)`
    margin-top: 4px;
    margin-bottom: 4px;
`;

export const StyledCalendarSection = styled.div<{ $isDateRangeDisabled: boolean }>`
    width: 60%;
    position: relative;
    border-left: 1px solid ${colors.gray200};

    .rdrCalendarWrapper {
        width: 100%;
        background-color: ${({ $isDateRangeDisabled }) => ($isDateRangeDisabled ? colors.gray100 : undefined)};

        .rdrDateInput {
            background-color: ${({ $isDateRangeDisabled }) => ($isDateRangeDisabled ? colors.gray100 : undefined)};

            input {
                cursor: ${({ $isDateRangeDisabled }) => ($isDateRangeDisabled ? 'default' : 'pointer')};
            }
        }

        .rdrMonth {
            width: 100%;
        }
    }
`;
export const StyledSwitchGroup = styled.div`
    display: flex;
    margin-top: 8px;

    span {
        margin-left: 8px;
    }
`;

type FakeSelectorProps = {
    color?: string;
    $disabled?: boolean;
};

export const StyledFakeSelector = styled.div<FakeSelectorProps>`
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 4px 11px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;

    color: ${({ $disabled }) => ($disabled ? rgba(0, 0, 0, 0.25) : 'inherit')};
    background: ${({ $disabled }) => ($disabled ? rgba(0, 0, 0, 0.04) : '#fff')};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

    :focus {
        border-color: ${({ color, $disabled }) => ($disabled ? '#d9d9d9' : color)};
        outline: none;
    }

    :hover {
        border-color: ${({ color, $disabled }) => ($disabled ? '#d9d9d9' : color)};
    }
`;
export const StyledRadioFieldGroup = styled(RadioFieldGroup)<RadioFieldGroupProps & { followContext: boolean }>`
    ${({ followContext }) =>
        followContext &&
        `
      label:nth-of-type(1) {
          width: 100px;
          padding:0px;
      }
      label:nth-of-type(2) {
          width: 70px;
          padding:0px;
      }
      label:nth-of-type(3) {
          width: 70px;
          padding:0px;
      }
  `}
`;
